import React, { useState } from "react";
import PropTypes from "prop-types";
import moment from "moment";
import { useDispatch, useSelector } from "react-redux";
import isEmpty from "lodash/isEmpty";

// constants
import { PENDING_DOCUMENT_OPTIONS, DEFALUT_PENDING_DOCUMENT_LIST } from "@/props-constants/dashboard-props";

// components
import CheckBoxGroup from "@/components/shared/CheckBoxGroup";
import InputBox from "@/components/shared/InputBox";

// mui
import {
    Box,
    Button,
    CircularProgress,
    InputAdornment,
    MenuItem,
    TextField,
    Typography,
    makeStyles,
    withStyles,
} from "@material-ui/core";
import Clear from "@material-ui/icons/Clear";

// actions
import { updateMeeting, createMeeting, sendDocsToWhatsApp } from "@/api";
import { showToasterMessage } from "@/store/actions/commonActions";
import { updateMeetingInStore } from "@/store/actions/meetingsActions";

// utils
import {
    getDataWithCorrectTimeSlot,
    getMeetingDetailsFromForm,
    NO_TIMESLOT_TEXT,
    timeSlotOptions,
} from "@/utils/slotFinder";
import { checkAndGetCredentialsFromSession } from "@/utils/session";

export const useStyles = makeStyles((theme) => ({
    root: {
        display: "flex",
        flexDirection: "column",
        gap: theme.spacing(1),
    },
    inputLabel: {
        fontWeight: 600,
        color: theme.palette.common.black,
    },
    menuItem: {
        fontWeight: 500,
        fontSize: "16px",
    },
    inputBoxContainer: {
        width: "100%",
        margin: theme.spacing(0, 0, 1),
    },
    marginRight: {
        marginRight: "16px",
    },
}));

export const textFieldStyleWrapper = (Component) =>
    withStyles((theme) => ({
        root: {
            "& .MuiOutlinedInput-input": {
                padding: "10px 14px",
                fontWeight: 500,
                fontSize: "inherit",
                "&:-webkit-autofill": {
                    WebkitBoxShadow: `0 0 0 100px ${theme.palette.common.white} inset`,
                },
            },

            "& .MuiFormHelperText-root": {
                margin: 0,
                fontSize: "12px",
            },

            "& .MuiInputAdornment-root": {
                fontSize: "inherit",
                cursor: "pointer",
            },

            "& .MuiOutlinedInput-root": {
                height: "40px",
                fontSize: "16px",
                borderRadius: "3px",

                "& fieldset": {
                    borderColor: "#6D6D6D",
                },
                "&:hover fieldset": {
                    borderColor: "#6D6D6D",
                },
                "&.Mui-focused fieldset": {
                    borderColor: "#6D6D6D",
                },
            },

            "& input[type=number]": {
                MozAppearance: "textfield",
            },
            "& input[type=number]::-webkit-outer-spin-button": {
                WebkitAppearance: "none",
                margin: 0,
            },
            "& input[type=number]::-webkit-inner-spin-button": {
                WebkitAppearance: "none",
                margin: 0,
            },
        },
    }))(Component);

const StyledTextField = textFieldStyleWrapper(TextField);

const AddNoteButton = withStyles((theme) => ({
    root: {
        color: theme.palette.success.main,
        borderColor: theme.palette.success.main,
        "&:hover": {
            color: theme.palette.common.white,
            background: "#00C853",
            borderColor: "#00C853",
        },
    },
}))(Button);

const defaultMeetData = {
    meeting_date: "",
    time_slot: "",
    meeting_type: "VIRTUAL",
};

export default function Meeting({ changeTab, studentData }) {
    const classes = useStyles();
    const dispatch = useDispatch();

    const defaultData = useSelector((state) =>
        state.meetings.data && !isEmpty(state.meetings.data) ? state.meetings.data[0] : null
    );
    const counsellorSlotsToShow = useSelector((state) => {
        const counsellorMeets = state.meetings.counsellorMeetings;
        if (isEmpty(counsellorMeets)) return [];

        return counsellorMeets.reduce((requiredArr, currentMeeting) => {
            if (currentMeeting.user_id === studentData.userId) {
                const istStartTime = moment(currentMeeting.from_datetime).add(5.5, "hours");
                const meetingDate = istStartTime.format("YYYY-MM-DD");
                const meetingTime = istStartTime.format("hh:mm");
                requiredArr.push({ meetingDate, meetingTime });
            }
            return requiredArr;
        }, []);
    });

    const [loading, setLoading] = React.useState(false);
    const [documents, setDocuments] = useState([...DEFALUT_PENDING_DOCUMENT_LIST]);
    const [defaultSelectedOptions, setDefaultSelectedOptions] = useState([...DEFALUT_PENDING_DOCUMENT_LIST]);
    const [note, setNote] = useState("");
    const [meetingFormData, setMeetingFormData] = React.useState(defaultMeetData);

    const onChangeHandler = (data) => {
        setMeetingFormData(getDataWithCorrectTimeSlot(data, false, counsellorSlotsToShow));
    };

    const meetingProps = React.useMemo(
        () => ({ options: timeSlotOptions(meetingFormData.meeting_date, counsellorSlotsToShow) }),
        [meetingFormData.meeting_date, counsellorSlotsToShow]
    );

    const updateData = (type) => (e) => {
        onChangeHandler({ ...meetingFormData, [type]: e.target.value });
    };

    const createNewMeeting = (meetingDetails) => {
        let _meetingDetails = getMeetingDetailsFromForm(meetingDetails);
        return createMeeting({ ..._meetingDetails });
    };

    const updateCurrentMeeting = (meetingID, updates) => {
        const meetingDetails = getMeetingDetailsFromForm(updates);
        return updateMeeting(meetingID, { ...meetingDetails });
    };

    const sendDocsToWhatsAppMessage = (credentials, pendingDocuments) => {
        const payload = {
            pending_documents: pendingDocuments.join(", "),
            user_id: studentData.userId,
            registrant_code: studentData.registrantCode,
        };

        if (credentials) {
            payload.fair_id = credentials.fairID;
        }

        setLoading(true);

        sendDocsToWhatsApp(payload)
            .then((res) => {
                if (res) {
                    setMeetingFormData(defaultMeetData);
                    setDefaultSelectedOptions([]);
                    setDocuments([]);
                    setNote("");
                    changeTab();
                    dispatch(showToasterMessage("Message send successfully", "success"));
                }
            })
            .catch(() => {
                dispatch(showToasterMessage("Something went wrong", "error"));
            })
            .finally(() => {
                setLoading(false);
            });
    };

    const scheduleMeeting = (credentials, pendingDocuments) => {
        const meetingData = {
            ...meetingFormData,
            is_whatsapp: true,
            user_id: studentData.userId,
            registrant_code: studentData.registrantCode,
        };

        if (studentData.counsellorId) {
            meetingData.counsellor_id = studentData.counsellorId;
        }

        if (credentials) {
            meetingData.fair_id = credentials.fairID;
        }
        if (!isEmpty(pendingDocuments)) meetingData.pending_documents = pendingDocuments.join(", ");

        setLoading(true);

        let fn,
            args = [];
        if (defaultData && defaultData.id) {
            fn = updateCurrentMeeting;
            args = [defaultData.id, meetingData];
        } else {
            fn = createNewMeeting;
            args = [meetingData];
        }

        fn(...args)
            .then((res) => {
                if (res) {
                    setMeetingFormData(defaultMeetData);
                    setDefaultSelectedOptions([]);
                    setDocuments([]);
                    setNote("");
                    changeTab();
                    dispatch(updateMeetingInStore(res.data));
                    dispatch(showToasterMessage("Meeting scheduled successfully", "success"));
                }
            })
            .catch(() => {
                dispatch(showToasterMessage("Something went wrong", "error"));
            })
            .finally(() => {
                setLoading(false);
            });
    };

    const onClickSubmit = () => {
        const credentials = checkAndGetCredentialsFromSession();
        const pendingDocuments = [...documents.filter((doc) => doc !== "Other")];
        if (note) {
            pendingDocuments.push(note);
        }

        if (meetingFormData.meeting_date || meetingFormData.time_slot) {
            if (!meetingFormData.meeting_date || !moment(meetingFormData.meeting_date).isValid()) {
                dispatch(showToasterMessage("Please enter valid date", "error"));
                return;
            }
            if (
                meetingFormData.time_slot === NO_TIMESLOT_TEXT ||
                !meetingProps.options.some((slot) => slot === meetingFormData.time_slot)
            ) {
                dispatch(showToasterMessage("Please enter valid time slot", "error"));
                return;
            }

            scheduleMeeting(credentials, pendingDocuments);
            return;
        }

        if (isEmpty(pendingDocuments)) {
            dispatch(showToasterMessage("Please select either date-time or Documents", "error"));
            return;
        }
        sendDocsToWhatsAppMessage(credentials, pendingDocuments);
    };

    const handleCheckboxBoxChange = (values) => {
        setDocuments(
            Object.values(values)
                .filter((option) => option.selected)
                .map((option) => option.value)
        );
    };

    const handleInputBoxChange = (event) => {
        setNote(event.target.value);
    };

    return (
        <Box className={classes.root}>
            <Typography variant="h4" className={classes.inputLabel}>
                {"Schedule Appointment"}
            </Typography>
            <StyledTextField
                id="date"
                label=""
                type="date"
                fullWidth
                variant="outlined"
                onChange={updateData("meeting_date")}
                InputLabelProps={{
                    shrink: true,
                }}
                InputProps={
                    meetingFormData.meeting_date
                        ? {
                              endAdornment: (
                                  <InputAdornment position="end">
                                      <Clear
                                          onClick={() =>
                                              setMeetingFormData((prev) => ({
                                                  ...prev,
                                                  meeting_date: "",
                                              }))
                                          }
                                      />
                                  </InputAdornment>
                              ),
                          }
                        : {}
                }
                value={meetingFormData.meeting_date || ""}
            />
            <StyledTextField
                type="search"
                variant="outlined"
                select
                fullWidth
                value={meetingFormData.time_slot || ""}
                onChange={updateData("time_slot")}
                InputProps={
                    meetingFormData.time_slot
                        ? {
                              endAdornment: (
                                  <InputAdornment position="end" className={classes.marginRight}>
                                      <Clear
                                          onClick={() =>
                                              setMeetingFormData((prev) => ({ ...prev, time_slot: "" }))
                                          }
                                      />
                                  </InputAdornment>
                              ),
                          }
                        : {}
                }
                SelectProps={{
                    displayEmpty: true,
                }}
            >
                <MenuItem disabled value="" className={classes.menuItem}>
                    {"Select Time Slot"}
                </MenuItem>
                {meetingProps.options.map((option) => (
                    <MenuItem key={option} value={option} className={classes.menuItem}>
                        {option}
                    </MenuItem>
                ))}
            </StyledTextField>

            <Box mt={1}>
                <Typography variant="h4" className={classes.inputLabel}>
                    {"Documents"}
                </Typography>
                <CheckBoxGroup
                    onCheck={handleCheckboxBoxChange}
                    checkBoxOptions={PENDING_DOCUMENT_OPTIONS.ENUMS}
                    defaultSelectedOptions={defaultSelectedOptions}
                    resetSelectedOptions={defaultSelectedOptions}
                />
                {documents.includes("Other") && (
                    <InputBox
                        classes={{
                            cInputBoxContainer: classes.inputBoxContainer,
                            cInputLabel: classes.inputLabel,
                        }}
                        multiline={true}
                        onChange={handleInputBoxChange}
                        onCheck={handleCheckboxBoxChange}
                        defaultValue={note}
                        rows={3}
                        value={note}
                        disabled={!documents.includes("Other")}
                    />
                )}
                <AddNoteButton variant="outlined" fullWidth={true} onClick={onClickSubmit}>
                    {loading ? <CircularProgress color={"primary"} size={30} /> : "Save"}
                </AddNoteButton>
            </Box>
        </Box>
    );
}

Meeting.propTypes = {
    studentData: PropTypes.object,
    changeTab: PropTypes.func,
};
